import { laneTopRem, resolveGanttLayout } from "@/ds";
import { useMediaQuery } from "@/hooks";

import {
  DIAS_POR_MES,
  INDISPONIBILIDADE_MIN_PX,
  ROTULO_EXTERNO_PX,
  withSimulacaoBadge,
} from "../gantt-carga/gantt-rows";
import { useGanttTip } from "../gantt-carga/hooks/use-gantt-tip";
import { CARGA_TRABALHO_MOBILE_QUERY } from "../gantt-carga/mobile";
import type {
  AllocationBarItem,
  AllocationBarProps,
  AllocationBarViewModel,
} from "./types-gantt-allocation-bar";

/**
 * Ícone (14px), respiro e "Nd" (3 dígitos): abaixo disso a barra mostra só o ícone. Os px por dia vêm do mês do preset
 * (`GANTT_LAYOUT_PRESETS`); `diasReservadosDaIndisponibilidade` (`gantt-rows.ts`) reserva as faixas pelo mês mobile, o mais estreito.
 */
const LARGURA_MINIMA_COM_TEXTO_PX = 56;

/** O mês efetivo é o do breakpoint: no mobile o preset usa `monthWidthMobileRem` (3,8125rem no compact), não os 6rem. */
function pxPorDiaDe(layout: AllocationBarProps["layout"], isMobile: boolean): number {
  const { monthWidthRem, monthWidthMobileRem } = resolveGanttLayout(layout);
  return ((isMobile ? monthWidthMobileRem : monthWidthRem) * 16) / DIAS_POR_MES;
}

function cabeTexto(
  item: AllocationBarItem,
  layout: AllocationBarProps["layout"],
  isMobile: boolean,
): boolean {
  if (item.kind !== "indisponibilidade" || item.dias === undefined) return true;
  return item.dias * pxPorDiaDe(layout, isMobile) >= LARGURA_MINIMA_COM_TEXTO_PX;
}

/**
 * O "Nd" da pílula estreita sai à direita dela. Encostada no fim da linha do tempo (o rótulo passaria de 100%), vai à
 * esquerda. Os px por % da linha saem da própria barra: `dias × px/dia ÷ largura em %`.
 */
function rotuloExternoDe(
  item: AllocationBarItem,
  layout: AllocationBarProps["layout"],
  isMobile: boolean,
): AllocationBarViewModel["rotuloExterno"] {
  if (cabeTexto(item, layout, isMobile) || item.dias === undefined) return null;
  const medidas = resolveGanttLayout(layout);
  const pxPorPercentual = (item.dias * pxPorDiaDe(layout, isMobile)) / Math.max(item.width, 0.01);
  const rotuloPercentual = ROTULO_EXTERNO_PX / pxPorPercentual;
  // A pílula é desenhada com `min-width` de 2rem: o fim dela é o maior entre a largura real e esse mínimo.
  const larguraDaPilula = Math.max(item.width, INDISPONIBILIDADE_MIN_PX / pxPorPercentual);
  const lado = item.left + larguraDaPilula + rotuloPercentual > 100 ? "esquerda" : "direita";
  const posicao =
    lado === "direita"
      ? { left: `calc(${item.left}% + max(${item.width}%, ${INDISPONIBILIDADE_MIN_PX / 16}rem))` }
      : { right: `${100 - item.left}%` };

  return {
    texto: item.label,
    lado,
    style: {
      ...posicao,
      top: `${laneTopRem(medidas, item.lane ?? 0)}rem`,
      height: `${medidas.barHeightRem}rem`,
    },
  };
}

function testIdOf(item: AllocationBarItem): string {
  if (item.kind === "indisponibilidade") return "gantt-indisponibilidade";
  if (item.transferida) return "gantt-bar-transferida";
  return item.cancelada ? "gantt-bar-cancelada" : "gantt-bar";
}

export function useGanttAllocationBar({
  item,
  layout,
  marca,
  marcaPreenchida = false,
  vessel = true,
  plainTitle = false,
}: AllocationBarProps): AllocationBarViewModel {
  const isMobile = useMediaQuery(CARGA_TRABALHO_MOBILE_QUERY);
  const { anchorProps, tip } = useGanttTip(
    marca ? `${marca.texto}\n\n${item.tooltip}` : item.tooltip,
    withSimulacaoBadge(item.card, Boolean(marca)),
  );
  // A obra transferida, além do tooltip, leva o texto no `title` (a revisão do Resolver Conflito).
  const ancora = plainTitle
    ? { title: marca ? `${marca.texto}\n\n${item.label}` : item.label }
    : { ...anchorProps, ...(item.transferida ? { title: item.tooltip } : null) };

  return {
    item,
    layout,
    comMarca: Boolean(marca),
    marcaPreenchida,
    vessel,
    plainTitle,
    somenteIcone: !cabeTexto(item, layout, isMobile),
    rotuloExterno: rotuloExternoDe(item, layout, isMobile),
    common: {
      start: item.left,
      end: item.left + item.width,
      layout,
      lane: item.lane ?? 0,
      past: item.past,
      testId: testIdOf(item),
      "aria-label": item.rotulo?.nome ?? item.label,
      tabIndex: 0,
      ...ancora,
    },
    tip,
  };
}
